import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Form,
  Table,
} from "react-bootstrap";
import Breadcrumb from "Common/BreadCrumb";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import logoDark from "../../../assets/images/anjal-gold-logo.png";
import logoLight from "../../../assets/images/anjal-gold-logo.png";
import {
  InvoiceData,
  InvoiceItem,
  Order,
  OrderProduct,
  OrderStatusHistoryItem,
} from "interface";
import { generateInvoicePDF, getOrderById } from "api/apis";

const OrdersOverview = () => {
  const { id } = useParams<{ id?: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCurrency] = useState("₹");
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    street: "",
    city: "",
    postalCode: "",
    state: "",
  });
  const [shippingAddress, setShippingAddress] = useState({
    street: "",
    city: "",
    postalCode: "",
    state: "",
  });
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
  });
  const [items, setItems] = useState([{ id: 1 }]);

  const STATUS_FLOW = [
    "Not processed",
    "Processing",
    "Shipped",
    "Delivered",
    "Cancelled",
  ];

  const STATUS_LABELS: { [key: string]: string } = {
    Not_processed: "Not processed",
    Processing: "Processing",
    Shipped: "Shipped",
    Delivered: "Delivered",
    Cancelled: "Cancelled",
  };

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      try {
        const fetchedOrder = await getOrderById(id);
        console.log("Order: ", fetchedOrder);
        setOrder(fetchedOrder);

        // Set customer info from order
        if (fetchedOrder.user) {
          setCustomerInfo({
            name: `${fetchedOrder.user.firstName || ""} ${
              fetchedOrder.user.lastName || ""
            }`.trim(),
            email: fetchedOrder.user.email || "",
          });
        }

        // Set address info if available
        if (fetchedOrder.address && typeof fetchedOrder.address !== "string") {
          setBillingAddress({
            street: fetchedOrder.address.address || "",
            city: fetchedOrder.address.city || "",
            postalCode: fetchedOrder.address.zipcode || "",
            state: fetchedOrder.address.state || "",
          });
          setShippingAddress({
            street: fetchedOrder.address.address || "",
            city: fetchedOrder.address.city || "",
            postalCode: fetchedOrder.address.zipcode || "",
            state: fetchedOrder.address.state || "",
          });
        }

        // Set items from order products
        if (fetchedOrder.products) {
          setItems(
            fetchedOrder.products.map((_, index) => ({ id: index + 1 }))
          );
        }
      } catch (error) {
        console.error("Failed to fetch order:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  document.title = "Order Overview";

  const handleDownloadInvoice = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!order) {
      console.error("No order data available");
      return;
    }

    try {
      // Prepare base invoice data
      const invoiceData: InvoiceData = {
        invoiceNumber: `INV-${order._id.slice(-6).toUpperCase()}`,
        date: moment(order.created).format("D M, Y"),
        paymentStatus: "Paid",
        companyName: "Anjal Gold",
        companyAddress: "123 Business Street, Mumbai, Maharashtra 400001",
        companyPostalCode: "400001",
        registrationNumber: "GSTIN123456789",
        companyEmail: "info@anjalgold.com",
        companyWebsite: "www.anjalgold.com",
        companyPhone: "+91 9876543210",
        clientName: customerInfo.name,
        clientAddress: `${shippingAddress.street}, ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.postalCode}`,
        clientEmail: customerInfo.email,
        billingAddress: `${billingAddress.street}, ${billingAddress.city}, ${billingAddress.state} ${billingAddress.postalCode}`,
        shippingAddress: `${shippingAddress.street}, ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.postalCode}`,
        items: [],
        subtotal: order.chargesBreakdown?.subtotal || 0,
        tax: order.chargesBreakdown?.charges?.tax?.amount || 0,
        discount: order.coupon?.discountAmount || 0,
        total: order.chargesBreakdown?.finalTotal || 0,
        currency: isCurrency,
        paymentMethod: order.paymentMethod || "Cash",
        paymentDetails:
          order.paymentMethod === "Card" ? "Card ending in XXXX" : "",
        notes:
          "All gold items are hallmarked as per government regulations. Goods once sold will not be taken back or exchanged.",
      };

      // Process products and include karat information
      if (order.products && order.products.length > 0) {
        invoiceData.items = order.products.map((productItem) => {
          const product = productItem.product;

          // Calculate price per gram safely
          const pricePerGram = product?.weightInGrams
            ? productItem.purchasePrice / product.weightInGrams
            : 0;

          return {
            description: product?.name || "Unnamed Product",
            details: product?.description || "",
            karat: product?.karat || "22K", // Karat info included here
            weight: product?.weightInGrams || 0,
            price: pricePerGram,
            basePrice: product?.basePrice || 0,
            huidCharges: product?.huidCharges || 0,
            labourCharges: product?.labourCharges || 0,
            miscCharges: product?.miscCharges || 0,
            rhodiumCharges: product?.rhodiumCharges || 0,
            quantity: productItem.quantity || 1,
            total: productItem.totalPrice || 0,
          };
        });
      } else {
        console.warn("No products found in the order");
      }

      // Generate the PDF invoice
      await generateInvoicePDF(invoiceData);
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
      // Consider adding user feedback here, like a toast notification
      // toast.error("Failed to generate invoice. Please try again.");
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumb title="Overview" pageTitle="Orders" />

          <Row className="mb-4 align-items-center">
            <Col>
              <h6 className="fs-18 mb-0">Order ID: {id}</h6>
            </Col>
            <Col className="col text-end">
              <Button variant="primary" onClick={handleDownloadInvoice}>
                <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                Download Invoice
              </Button>
            </Col>
          </Row>

          <Row>
            <Col xxl={4} lg={6}>
              <div className="card bg-success bg-opacity-10 border-0">
                <div className="card-body">
                  <div className="d-flex gap-3">
                    <div className="flex-grow-1">
                      <h6 className="fs-18 mb-3">Customer Info</h6>
                      <p className="mb-0 fw-medium">
                        {order?.firstName || order?.user?.firstName}{" "}
                        {order?.lastName || order?.user?.lastName}
                      </p>
                      <p className="mb-1">{order?.user?.email}</p>
                      <p className="mb-0">{order?.phone}</p>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <div className="avatar-title bg-success-subtle text-success rounded fs-3">
                        <i className="ph-user-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col xxl={4} lg={6}>
              <div className="card bg-primary bg-opacity-10 border-0">
                <div className="card-body">
                  <div className="d-flex gap-3">
                    <div className="flex-grow-1">
                      <h6 className="fs-18 mb-3">Shipping Address</h6>
                      {typeof order?.address === "string" ? (
                        <p className="mb-0">{order.address}</p>
                      ) : (
                        <>
                          <p className="mb-0">
                            {order?.address?.firstName}{" "}
                            {order?.address?.lastName}
                          </p>
                          <p className="mb-0">{order?.address?.phone}</p>
                          <p className="mb-0">{order?.address?.address}</p>
                          <p className="mb-0">{order?.address?.zipcode}</p>
                          <p className="mb-1">{order?.address?.city}</p>
                          <p className="mb-0">{order?.address?.country}</p>
                        </>
                      )}
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <div className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                        <i className="ph-map-pin"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            <Col xxl={4} lg={6}>
              <div className="card card-primary overflow-hidden">
                <div className="position-absolute end-0 start-0 bottom-0 z-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                    // xmlns:xlink="http://www.w3.org/1999/xlink"
                    width="400"
                    height="250"
                    preserveAspectRatio="none"
                    viewBox="0 0 400 250"
                  >
                    <g mask='url("#SvgjsMask1551")' fill="none">
                      <path
                        d="M306 65L446 -75"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M399 2L315 86"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M83 77L256 -96"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M281 212L460 33"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M257 62L76 243"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M305 123L214 214"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M327 222L440 109"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M287 109L362 34"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M259 194L332 121"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M376 186L240 322"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M308 153L123 338"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M218 62L285 -5"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                    </g>
                    <defs>
                      <mask id="SvgjsMask1551">
                        <rect width="400" height="250" fill="#ffffff"></rect>
                      </mask>
                      <linearGradient
                        x1="100%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                        id="SvgjsLinearGradient1558"
                      >
                        <stop
                          stopColor="rgba(var(--tb-white-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-white-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                      <linearGradient
                        x1="0%"
                        y1="100%"
                        x2="100%"
                        y2="0%"
                        id="SvgjsLinearGradient1559"
                      >
                        <stop
                          stopColor="rgba(var(--tb-white-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-white-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div className="card-body position-relative">
                  <h5 className="card-title fw-medium">Add New Orders</h5>
                  <p className="text-white-75">
                    Orders are one of the main building blocks of creating an an
                    ad campaign in Ad Manager.
                  </p>
                  <Link
                    to="/orders-list-view"
                    className="link-light text-white"
                  >
                    Create Order <i className="ph-arrow-right align-middle"></i>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xxl={9}>
              <Card>
                <div className="card-body">
                  <div className="table-responsive table-card">
                    <table className="table align-middle table-nowrap mb-0">
                      <thead className="text-muted table-light">
                        <tr>
                          <th scope="col">Product ID</th>
                          <th scope="col">Product Name</th>
                          {/* <th scope="col">Base Price</th> */}
                          <th scope="col">Karat</th>
                          <th scope="col">Weight(in grams)</th>
                          <th scope="col">Quantity</th>
                          <th scope="col" className="text-end">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {order?.products.map(
                          (item: OrderProduct, index: number) => (
                            <tr key={index}>
                              <td>
                                <Link
                                  to="#!"
                                  className="fw-medium link-primary"
                                >
                                  #{item?.variantSku}
                                </Link>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="flex-shrink-0 me-2">
                                    <img
                                      src={
                                        item?.product?.images?.[0] ??
                                        "/placeholder.jpg"
                                      }
                                      alt=""
                                      className="avatar-xs rounded-circle"
                                    />
                                  </div>
                                  <div className="flex-grow-1">
                                    {item.product?.name}
                                  </div>
                                </div>
                              </td>
                              {/* <td>
                                <span className="text-secondary">
                                  ₹{item?.purchasePrice}
                                </span>
                              </td> */}
                              <td>{item.product.karat}</td>
                              <td>{item.weight} g</td>
                              <td>{item.quantity} PCS</td>
                              {/* <td className="text-end">₹{item.basePrice}</td> */}
                              <td className="text-end">
                                ₹
                                {Number(
                                  order?.priceBreakdown?.final_total?.value ?? 0
                                ).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          )
                        )}

                        <tr>
                          <td colSpan={7} className="p-0">
                            <table className="table table-borderless mb-0">
                              <tbody>
                                {order?.priceBreakdown &&
                                  Object.entries(order.priceBreakdown).map(
                                    ([key, breakdown]) => (
                                      <tr key={key}>
                                        <td className="align-middle">
                                          {breakdown.label}
                                          {key.startsWith("coupon_discount") &&
                                            order?.coupon?.code && (
                                              <span
                                                style={{
                                                  backgroundColor: "#f0f0f0",
                                                  borderRadius: "12px",
                                                  padding: "2px 8px",
                                                  fontSize: "0.85rem",
                                                  marginLeft: "8px",
                                                  color: "#333",
                                                  display: "inline-block",
                                                }}
                                              >
                                                {order.coupon.code}
                                              </span>
                                            )}
                                        </td>
                                        <td
                                          className={`text-end ${
                                            breakdown.type === "total" ||
                                            breakdown.type === "grandTotal"
                                              ? "fw-semibold"
                                              : ""
                                          }`}
                                        >
                                          ₹
                                          {Number(
                                            breakdown.value
                                          ).toLocaleString("en-IN", {
                                            minimumFractionDigits: 2,
                                          })}
                                        </td>
                                      </tr>
                                    )
                                  )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xxl={3}>
              <Row>
                <Col lg={12}>
                  <Card>
                    <div className="card-header d-flex align-items-center">
                      <h5 className="card-title flex-grow-1 mb-0">
                        Logistics Details
                      </h5>
                      <div className="flex-shrink-0">
                        <Link to="#track-order" className="btn btn-sm btn-info">
                          Track Order
                        </Link>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="text-center">
                        <i className="bi bi-truck fs-1"></i>
                        <h5 className="fs-18">Anjal Logistics</h5>
                        <p className="mb-0">ID: MFDS1400457854</p>
                        <p className="mb-0">
                          Payment Mode : {order?.paymentMethod}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col lg={12}>
                  <Card>
                    <div className="card-header">
                      <h5 className="card-title mb-0">Payment Detail:</h5>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-sm table-borderless align-middle description-table mb-0">
                          <tbody>
                            <tr>
                              <td>Transactions:</td>
                              <td>
                                <span className="fw-medium">
                                  #TNR124561278124
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td>Payment Method</td>
                              <td>
                                <span className="fw-medium">
                                  {order?.paymentMethod}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td>Card Number:</td>
                              <td>
                                <span className="fw-medium">
                                  XXXX XXXX XXXX 3028
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td>Card Holder Name</td>
                              <td>
                                <span className="fw-medium">Vinod Kumar</span>
                              </td>
                            </tr>
                            <tr>
                              <td>Total Amount</td>
                              <td>
                                <span className="fw-medium">
                                  ₹{order?.total}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Col>

            {order?.status && (
              <Col lg={12}>
                <Card id="track-order">
                  <div className="card-header d-flex align-items-center gap-3">
                    <h5 className="card-title flex-grow-1 mb-0">
                      Order Status
                    </h5>
                    <div className="flex-shrink-0">
                      <Button
                        variant="soft-primary"
                        size="sm"
                        className="btn btn-sm mt-2 mt-sm-0 me-1"
                      >
                        <i className="ri-map-pin-line align-bottom me-1"></i>{" "}
                        Change Address
                      </Button>
                      <Button
                        variant="soft-danger"
                        size="sm"
                        className="btn btn-sm mt-2 mt-sm-0"
                      >
                        <i className="mdi mdi-archive-remove-outline align-bottom me-1"></i>{" "}
                        Cancel Order
                      </Button>
                    </div>
                  </div>

                  <div className="card-body">
                    <Row
                      className="d-flex order-tracking-wrapper flex-lg-row flex-column align-items-lg-stretch align-items-center"
                      style={{ position: "relative" }}
                    >
                      {STATUS_FLOW.map((statusKey, index) => {
                        const currentStatusIndex = STATUS_FLOW.indexOf(
                          order.status
                        );
                        const isCompleted = index <= currentStatusIndex;
                        const historyItem = order.statusHistory?.find(
                          (s: OrderStatusHistoryItem) => s.status === statusKey
                        );

                        return (
                          <Col
                            lg={2}
                            xs={12}
                            key={statusKey}
                            className={`order-tracking d-flex flex-column align-items-center position-relative text-center ${
                              isCompleted ? "completed" : ""
                            }`}
                          >
                            <span className="is-complete"></span>
                            <div className="card mt-3 mb-0 w-100">
                              <div className="card-body">
                                <h6 className="fs-17">
                                  {STATUS_LABELS[statusKey] || statusKey}
                                </h6>
                                <p className="text-muted fs-15 mb-0">
                                  {historyItem
                                    ? moment(historyItem.changedAt).format(
                                        "ddd, DD MMM, YYYY"
                                      )
                                    : isCompleted
                                    ? "Completed"
                                    : "Pending"}
                                </p>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OrdersOverview;
